import { Dialog, LoadingSpinner, PlainButton } from "@jugl-web/ui-components";
import { Button } from "@jugl-web/ui-kit";
import { useRestApiProvider } from "@jugl-web/rest-api";
import type { CalendlyEventType, SquareupLocation } from "@jugl-web/rest-api";
import { useToast, useAppVariant, useTranslations } from "@jugl-web/utils";
import React, { useMemo, useState } from "react";
import { ReactComponent as CloseIcon } from "../../../CXActionsList/components/ActionsPickerDialog/assets/close.svg";

type ConnectionProvider = "calendly" | "squareup";

interface PickerItem {
  id: string;
  name: string;
  duration?: number;
}

export interface ConnectionPickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  provider: ConnectionProvider;
  entityId: string;
  connectionId: string;
}

export const ConnectionPickerDialog: React.FC<ConnectionPickerDialogProps> = ({
  isOpen,
  onClose,
  provider,
  entityId,
  connectionId,
}) => {
  const { t } = useTranslations();
  const { variant } = useAppVariant();
  const { toast } = useToast({ variant });
  const { integrationsApi } = useRestApiProvider();
  const [setCalendlyEventType, { isLoading: isSavingCalendly }] =
    integrationsApi.useSetCalendlyEventTypeMutation();
  const [setSquareupLocation, { isLoading: isSavingSquareup }] =
    integrationsApi.useSetSquareupLocationMutation();
  const eventTypesQuery = integrationsApi.useGetCalendlyEventTypesQuery(
    { entityId, connectionId },
    { skip: provider !== "calendly" }
  );
  const locationsQuery = integrationsApi.useGetSquareupLocationsQuery(
    { entityId, connectionId },
    { skip: provider !== "squareup" }
  );

  const items: PickerItem[] = useMemo(() => {
    if (provider === "calendly") return eventTypesQuery.data?.data ?? [];
    return locationsQuery.data?.data ?? [];
  }, [provider, eventTypesQuery.data, locationsQuery.data]);

  const isFetching =
    provider === "calendly"
      ? eventTypesQuery.isFetching
      : locationsQuery.isFetching;

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const isSaving = isSavingCalendly || isSavingSquareup;

  const title =
    provider === "calendly"
      ? t({
          id: "cx-actions-page.calendly-event-type",
          defaultMessage: "Select an event type",
        })
      : t({
          id: "cx-actions-page.squareup-location",
          defaultMessage: "Select a location",
        });

  const handleConfirm = async () => {
    const selected = items.find((item) => item.id === selectedId);
    if (!selected) return;

    try {
      if (provider === "calendly") {
        const payload: CalendlyEventType = {
          id: selected.id,
          name: selected.name,
          duration: selected.duration ?? 0,
        };
        await setCalendlyEventType({
          entityId,
          connectionId,
          eventType: payload,
        }).unwrap();
      } else {
        const payload: SquareupLocation = {
          id: selected.id,
          name: selected.name,
        };
        await setSquareupLocation({
          entityId,
          connectionId,
          location: payload,
        }).unwrap();
      }
      toast(
        t({
          id: "cx-actions-page.connection-success",
          defaultMessage: "Connected successfully",
        })
      );
      onClose();
    } catch {
      toast(
        t({
          id: "cx-actions-page.connection-save-failed",
          defaultMessage: "Failed to save selection. Please try again.",
        })
      );
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="flex w-full max-w-[520px] flex-col"
    >
      <div className="flex h-[60px] shrink-0 items-center justify-between gap-6 px-6">
        <span className="font-secondary font-semibold text-[#383838]">
          {title}
        </span>
        <PlainButton onClick={onClose}>
          <CloseIcon />
        </PlainButton>
      </div>
      <div className="jugl__custom-scrollbar flex max-h-[420px] flex-col gap-2 overflow-y-auto px-6 pb-4">
        {isFetching && (
          <div className="flex justify-center py-6">
            <LoadingSpinner size="sm" />
          </div>
        )}
        {!isFetching && items.length === 0 && (
          <div className="text-grey py-6 text-center text-sm">
            {t({
              id: "cx-actions-page.picker-empty",
              defaultMessage: "No items available",
            })}
          </div>
        )}
        {!isFetching &&
          items.map((item) => {
            const isSelected = selectedId === item.id;
            return (
              <button
                type="button"
                key={item.id}
                onClick={() => setSelectedId(item.id)}
                className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                  isSelected
                    ? "border-primary-500 bg-primary-50"
                    : "hover:bg-grey-100 border-transparent bg-[#F9F9FA]"
                }`}
              >
                <div className="flex flex-col">
                  <span className="text-dark text-sm font-medium">
                    {item.name}
                  </span>
                  {typeof item.duration === "number" && item.duration > 0 && (
                    <span className="text-grey text-xs">
                      {t(
                        {
                          id: "cx-actions-page.event-duration-minutes",
                          defaultMessage: "{duration} min",
                        },
                        { duration: item.duration }
                      )}
                    </span>
                  )}
                </div>
                <div
                  className={`h-4 w-4 rounded-full border ${
                    isSelected
                      ? "border-primary-500 bg-primary-500"
                      : "border-grey-400"
                  }`}
                />
              </button>
            );
          })}
      </div>
      <div className="flex justify-end gap-3 px-6 py-4">
        <Button variant="secondary" onClick={onClose} isDisabled={isSaving}>
          {t({ id: "common.cancel", defaultMessage: "Cancel" })}
        </Button>
        <Button
          onClick={handleConfirm}
          isDisabled={!selectedId || isSaving}
          isLoading={isSaving}
        >
          {t({
            id: "cx-actions-page.picker-submit",
            defaultMessage: "Confirm",
          })}
        </Button>
      </div>
    </Dialog>
  );
};
